import * as Moment from "moment";
import * as React from 'react';
import {Column} from 'react-data-grid';
import {translate} from "react-i18next";
import {FlagNameValues, MenuItemProps} from "semantic-ui-react";
import {
    IVirtualizedTableColumn,
    TData,
    TDataValue,
    ITextExportStatus,
    AssignmentTypeFilter
} from '../../components/Table/VirtualizedTableInterfaces';
import {ITranslatedComponent} from "../../redux/reducers/generalReducer";
import {
    WorkflowStatusDtoType,
    RoleDto,
    OptionDtoSpecificType,
    OptionTextDto,
    ResourceListOfOptionDto, ModeltypeBasicDto, OptionDto, FlagDto, LanguageDto
} from "../../services/Client";
import {IPSLApproval, IRowStatusProps} from "../../services/WorkflowUtils";
import DataAdapterOptionsView from './DataAdapterOptionsView';
import {extractMultiFields, newGridIntialColumns, OptionViewUtils, PDiPExcludedOptionTypes, PropertiesKey, SpecificFilterEnum, validateTexts} from "./OptionsViewUtils";
import _ from "lodash";
import OptionTable from "./OptionTable";
import {IPslSelection} from "./PSL/PslSelect";
import {OptionIndicatorsKey} from "./OptionColumns";
import {formatDate, SYSTEM_USER} from "../../components/Table/dataHandler/Utils";
import {LanguageCodeEnum} from "../AdminView/LanguageTab/LanguageTable";
import {getPSLColumnDefs} from "./AgGridOptionColumns";

interface OptionsTableContainerProps {
    showFilter?: boolean;
    data: DataAdapterOptionsView[];
    derivates: ModeltypeBasicDto[];
    derivateToColorMap: {[key: string]: string};
    columns: Column[];
    readonly: boolean;
    specificTypeOptionFilter: SpecificFilterEnum;
    assignmentTypeFilterValue: AssignmentTypeFilter;
    onCellClick?: (key: string, index: number, data: TDataValue, visibleDerivates?: string[]) => void;
    onChange?: (key: string, data: TDataValue, index: number) => void;
    onApprove: (data: IPSLApproval, index: number) => void;
    onChangeSelect: (index: number[]) => void;
    userRoles?: RoleDto[];
    inEditMode: boolean;
    loadedPSL?: IPslSelection;
    isSpecificMarket: boolean;
    exportExcel: boolean;
    postOptions?: ResourceListOfOptionDto;
    createIOspecificOption?: (key: string, index: number) => void;
    onOptionWFReset: (index: number) => void;
    onChangeWorkflow: (key: string, objectId: number, index: number) => void;
    setExportExcelFlags: (areFlagsEnabled: boolean) => void;
    flags?: FlagDto[];
    selectedLanguages: LanguageDto[];
    languageList: LanguageDto[] | undefined;
    userLanguage: string;
    isNewGridTable: boolean;
    page: number;
    pageSize: number;
    totalElements: number;
    setPageSize?: (e: React.MouseEvent<HTMLAnchorElement>, data: MenuItemProps) => void;
    onPageChange: (newPage: number, gridApi: any) => void;
    isLoadingData: boolean;
    onLazyLoad: (gridApi: any) => void;
    onNewFilterChange: (field: string, value: string) => void;
    onGridReadyParent: (params: any) => void;
    newDropDownFilters: any;
    records: number;
    filterCount: number;
    newGridFilters: any;
    updatable: boolean;
    onSelectionChanged?: () => void;
    onFilterByComparingAssignmentsChange: (values: number[]) => void;
    onFilterPccdSyncDataChange: (value: boolean | null) => void;
    generationsRows?: any[];
}
class OptionsTableContainer extends React.Component<OptionsTableContainerProps & ITranslatedComponent> {
    private utils: OptionViewUtils;
    constructor(props: OptionsTableContainerProps & ITranslatedComponent) {
        super(props);
        this.utils = new OptionViewUtils();
    }

    /**
     * Creates the respective rows for the table
     */
    private getRows() {
        const {t} = this.props;
        let rows: IVirtualizedTableColumn[] = [];
        let derivatesNames: Map<number, string> = DataAdapterOptionsView.createDerivatesList(this.props.derivates, this.props.userLanguage);
        if (this.props.isNewGridTable) {
            // @ts-ignore
            rows = getPSLColumnDefs(this.props, [], false, null, {}, undefined)
                .filter((item: any) => item.key !== '')
                .map((item: any) => ({
                    key: item.key,
                    label: item.headerName,
                    options: {
                        cellWidth: item.width,
                        showOnStart: newGridIntialColumns.includes(item.key),
                        derivateList: derivatesNames,

                    },
                })) as IVirtualizedTableColumn[];
        } else {
            rows = this.props.columns.map((item: Column) => {
                const derivateList = (item as any).derivates;
                const row: IVirtualizedTableColumn = {
                    key: item.key,
                    label: t(item.name.replace("Name_", "").toUpperCase()),
                    rowType: this.utils.getRowType(item.key),
                    options: {
                        cellWidth: this.utils.getColumnDefaultWidth(item.key),
                        showOnStart: true, // this.utils.isRowVisible(item.key),
                        derivateList: derivateList as Map<number, string>,
                        readOnly: this.utils.isRowReadOnly(item.key, this.props.readonly),
                        inputWithPopup: true,
                        isSortable: this.utils.isRowSortable(item.key)
                    }
                };
                return row;
            });
        }
        return rows;
    }

    /**
     * Creates the respective data for the table
     */
    private buildData(data: DataAdapterOptionsView[]): [TData[], OptionDto[], any[]] {
        const optionDtos: OptionDto[] = [];
        let generationsRows: any = [];
        const tableData = data.map((item: DataAdapterOptionsView): any => {
            const isDisabled: boolean = this.props.isSpecificMarket === true && item.cnr !== undefined && item.cnr.cnr === PropertiesKey.BasicMarketCnrKey ? true : false;
            const isSpecificOption: boolean = this.props.isSpecificMarket === true && item.cnr !== undefined && item.cnr.cnr !== PropertiesKey.BasicMarketCnrKey ? true : false;
            const itemHasSpecificCnr = item.cnr && item.cnr.cnr !== PropertiesKey.BasicMarketCnrKey;

            const statusView: IRowStatusProps = {
                statusValueState: item.statusValueState,
                statusValueStateEditable: item.statusValueStateEditable,
                statusValueStateResetable: item.specificType !== undefined && item.specificType === OptionDtoSpecificType.DEPENDENT_OVERWRITE ?
                    false : item.statusValueStateResetable,
                statusValueStates: item.statusValueStates,
                statusView: item.statusView,
                resetRoles: item.statusResetRoles,
                workFlowName: item.workFlowName,
                workflowType: item.workflowType,
                workflowResetPointRejection: item.workflowResetPointRejection,
                isDependentOverwriteSpecific: this.props.isSpecificMarket &&
                    itemHasSpecificCnr &&
                    item.specificType && item.specificType === OptionDtoSpecificType.DEPENDENT_OVERWRITE
                    ? true : false,
                invalidTexts: item.statusView === WorkflowStatusDtoType.MAINTENANCE ? !validateTexts(item.texts) : undefined,
                statusStepIndex: item.statusView === WorkflowStatusDtoType.MAINTENANCE ?
                    item.statusValueStates ? item.statusValueStates.findIndex((s) => s.id === item.statusValueState) : undefined : undefined,
                // available in BM PSLs or in SM PSLs only for IO/COPY specific options
                hasChangeWfButton: !this.props.isSpecificMarket ||
                    (itemHasSpecificCnr &&
                        item.specificType &&
                        (item.specificType === OptionDtoSpecificType.INDEPENDENT_OVERWRITE ||
                            item.specificType === OptionDtoSpecificType.COPY)
                    )
            };

            const exportStatus: ITextExportStatus = {
                textExportDate: item.textExportDate,
                textExportStatus: item.textExportStatus,
                textExportStatusMessage: item.textExportStatusMessage
            };

            optionDtos.push(item.dto);
            if (item.optionIndicators && item.optionIndicators.dto && item.optionIndicators.dto.modeltypes) {
                const modeltypes = item.optionIndicators.dto.modeltypes
                    .filter((modeltype) => modeltype.modeltypeBasic && modeltype.modeltypeBasic.generation !== undefined)
                    .map((modeltype) => {
                        return {
                            generation: modeltype.modeltypeBasic.generation,
                            objectId: modeltype.modeltypeBasic.objectId,
                            child: []
                        };
                    });

                if (modeltypes.length > 0) {
                    modeltypes.forEach((modeltype) => {
                        const isItemExist = generationsRows.find((generationRow: any) => generationRow.objectId === modeltype.objectId);
                        if (!isItemExist) {
                            const alreadyAddedGeneration = generationsRows.find((generationRow: any) => generationRow.generation.objectId === modeltype.generation.objectId);
                            if (!alreadyAddedGeneration) {
                                generationsRows.push(modeltype);
                            } else {
                                const alreadyNotAChild = alreadyAddedGeneration.child.find((child: any) => child.objectId === modeltype.objectId);
                                if (!alreadyNotAChild) {
                                    alreadyAddedGeneration.child.push(modeltype);
                                }
                            }
                        }
                    });
                }
            }
            return {
                optionBasicView: item.optionBasicView,
                derivateStates: item.derivateStates,
                availableEnd: item.availableEnd ? Moment(item.availableEnd, "DD.MM.YYYY", "de") : undefined,
                availableStart: item.availableStart ? Moment(item.availableStart, "DD.MM.YYYY", "de") : undefined,
                categoryText: item.categoryText.text.length > 0 ?
                    item.categoryText.text : item.optionBasic.category,
                comment: item.comment,
                constraint: item.constraint,
                price: item.price,
                optionTypeView: this.props.t("GLOBAL:" + item.optionTypeView) + " (" + item.optionTypeView + ')',
                optionType: item.optionBasic.optionType,
                statusView: statusView,
                shorttext: extractMultiFields(item, "shorttext"),
                longtext: extractMultiFields(item, "longtext"),
                shortPCCDtext: extractMultiFields(item, "shortPCCDtext"),
                longPCCDtext: extractMultiFields(item, "longPCCDtext"),
                // optionTextPCCDExports: item.dto.optionTextPCCDExports,
                footer: extractMultiFields(item, "footer"),
                usp: extractMultiFields(item, "usp"),
                pdiptext: item.optionBasic.pdiptext,
                exclusion: item.exclusion,
                packageContent: item.packageContent,
                flags: {
                    options: this.props.flags
                        ? this.props.flags.map(flag => ({text: flag.textLabel, value: flag.objectId}))
                        : [],
                    value: item.flags ? item.flags : [],
                },
                optionIndicators: item.optionIndicators,
                reference: item.reference,
                referenceExc: item.referenceExc,
                intSequence: item.intSequence,
                exportStatus: exportStatus,
                cnr: item.cnr,
                specificType: item.specificType,
                isDisabled: isDisabled,
                rowClass: isSpecificOption ? 'row-' + item.specificType :
                    PDiPExcludedOptionTypes.has(item.optionBasic.optionType) ? 'table-invalid-pdip-option-type-row' : 'table-invalid-pdip-row',
                parentOptionId: item.parentOptionId,
                objectId: item.objectId,
                pdipFlag: item.dto.pdipFlag,
                pccdFlag: item.dto.pccdFlag,
                missingPDiPAMTBs: item.getMissingPDiPAMTBs(),
                changedBy: item.changedBy === SYSTEM_USER ? this.props.t("GLOBAL:SYSTEM_USER") : item.changedBy,
                changedTimestamp: item.changedTimestamp ? formatDate(item.changedTimestamp) : undefined
                // add here wath you need to send
            };
        });
        generationsRows = generationsRows.sort((a: any, b: any) => a.generation.generation.localeCompare(b.generation.generation));
        return [tableData, optionDtos, generationsRows];
    }

    private getRole = (data: any) => {
        const tmpData: any = data;
        const currentStep = _.find(tmpData.statusView.statusValueStates, (element: any) => element.id === tmpData.statusView.statusValueState);
        const priv = this.props.userRoles ? this.props.userRoles.find((element: RoleDto) => element && element.name === currentStep.role) : undefined;
        return priv;
    }

    /**
     * @performace_notice this is executed for every cell in the table
     */
    private cellReadOnly = (key: string, data: any, readOnly: boolean) => {
        const item: any = data;

        if (key === 'pdiptext') {
            return true;
        }
        // Disable the workflow cell if the options is specific and of type DEPENDENT_OVERWRITE
        if (item.specificType !== undefined && item.parentOptionId !== undefined && item.specificType === OptionDtoSpecificType.DEPENDENT_OVERWRITE) {
            return (
                key === "derivateStates" ||
                key === "longtext" ||
                key === "shorttext" ||
                key === "footer" ||
                key === "usp" ||
                key === 'statusView' ||
                key === OptionIndicatorsKey
            );
        }
        if (item.statusView.statusView === WorkflowStatusDtoType.FREEZE) {

            if (!this.getRole(data))
                return true;
            return (
                key === "derivateStates" ||
                key === "longtext" ||
                key === "shorttext" ||
                key === "footer" ||
                key === "usp" ||
                key === OptionIndicatorsKey);
        }

        if (item.statusView.statusView === WorkflowStatusDtoType.APPROVED) {
            return (
                key === "statusView" ||
                key === "longtext" ||
                key === "shorttext" ||
                key === "footer" ||
                key === "usp" ||
                key === OptionIndicatorsKey);
        }
        if (item.statusView.statusView !== WorkflowStatusDtoType.MAINTENANCE && key === "derivateStates") {
            return !this.getRole(data);
        }

        if (item.statusView.statusView !== WorkflowStatusDtoType.MAINTENANCE) {
            return (
                key === "statusView" ||
                key === "longtext" ||
                key === "shorttext" ||
                key === "footer" ||
                key === "usp" ||
                key === OptionIndicatorsKey);
        }
        if (key === 'usp') {
            const role = this.getRole(data);
            if (role) {
                if (item.packageContent.length === 0) {
                    return false;
                }
                return true;
            }
            return !role;
        }
        if (this.props.userRoles) {
            return !this.getRole(data);
        }

        return readOnly;
    }

    /**
     * @performace_notice this is executed for every cell in the table
     */
    workFlowResetStateDisabled = (key: string, data: any, readOnly: boolean) => {
        if (readOnly) {
            return true;
        }

        if (this.props.userRoles) {
            const tmpData: any = data;
            const priv = _.find(tmpData.statusView.statusValueStates, (state: any) =>
                !!(this.props.userRoles && this.props.userRoles.find((element: RoleDto) => element && element.name === state.role)));
            return !priv;
        }
        return readOnly;
    }

    public render() {
        const [data, dtos, generationsRows] = this.buildData(this.props.data);

        const derivates = this.props.isSpecificMarket
            ? this.props.derivates.filter(der => der.pdipFlag === true)
            : this.props.derivates;

        return (
            <>
                <div className="full-height">
                    <OptionTable
                        isNewGridTable={this.props.isNewGridTable}
                        workFlowResetStateDisabled={this.workFlowResetStateDisabled}
                        exportExcel={this.props.exportExcel}
                        postOptions={this.props.postOptions}
                        inEditMode={this.props.inEditMode}
                        isSpecificMarket={this.props.isSpecificMarket}
                        rowSelectable="all"
                        specificTypeOptionFilter={this.props.specificTypeOptionFilter}
                        assignmentTypeFilterValue={this.props.assignmentTypeFilterValue}
                        onChangeSelect={this.props.onChangeSelect}
                        columnsData={this.getRows()}
                        data={data}
                        optionDtos={dtos}
                        availableDerivates={derivates}
                        derivateToColorMap={this.props.derivateToColorMap}
                        onCellClick={this.props.onCellClick}
                        onChange={this.props.onChange}
                        onApprove={this.props.onApprove}
                        loadedPSL={this.props.loadedPSL}
                        isCellReadOnly={this.cellReadOnly}
                        createIOspecificOption={this.props.createIOspecificOption}
                        onOptionWFReset={this.props.onOptionWFReset}
                        onChangeWorkflow={this.props.onChangeWorkflow}
                        setExportExcelFlags={this.props.setExportExcelFlags}
                        selectedLanguages={this.props.selectedLanguages}
                        generationsRows={this.props.isNewGridTable ? this.props.generationsRows : generationsRows}
                        page={this.props.page}
                        pageSize={this.props.pageSize}
                        totalElements={this.props.totalElements}
                        setPageSize={this.props.setPageSize}
                        onPageChange={this.props.onPageChange}
                        isLoadingData={this.props.isLoadingData}
                        onLazyLoad={this.props.onLazyLoad}
                        onNewFilterChange={this.props.onNewFilterChange}
                        onGridReadyParent={this.props.onGridReadyParent}
                        newDropDownFilters={this.props.newDropDownFilters}
                        flags={this.props.flags}
                        records={this.props.records}
                        filterCount={this.props.filterCount}
                        newGridFilters={this.props.newGridFilters}
                        updatable={this.props.updatable}
                        onFilterByComparingAssignmentsChange={this.props.onFilterByComparingAssignmentsChange}
                        onFilterPccdSyncDataChange={this.props.onFilterPccdSyncDataChange}
                        onSelectionChanged={this.props.onSelectionChanged}
                    />
                </div>
            </>
        );
    }
}
export default translate(["COLUMN_FACTORY"])(OptionsTableContainer);



// WEBPACK FOOTER //
// ./src/views/OptionsView/OptionsTableContainer.tsx